import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Mail, Printer, Eye } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useSharedData } from "./AgreementsPage";

interface ChecklistItem {
  id: string;
  text: string;
}

const ChecklistLetter = () => {
  const { toast } = useToast();
  const { checklistData: letterData, setChecklistData: setLetterData } = useSharedData();
  const [newItemText, setNewItemText] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const addChecklistItem = () => {
    if (newItemText.trim()) {
      const newItem: ChecklistItem = {
        id: Date.now().toString(),
        text: newItemText.trim()
      };
      setLetterData(prev => ({
        ...prev,
        checklistItems: [...prev.checklistItems, newItem]
      }));
      setNewItemText('');
    }
  };

  const removeChecklistItem = (id: string) => {
    setLetterData(prev => ({
      ...prev,
      checklistItems: prev.checklistItems.filter(item => item.id !== id)
    }));
  };

  const handlePrint = () => {
    window.print();
    toast({
      title: "Print Dialog Opened",
      description: "The letter is ready to print."
    });
  };

  const handleEmail = () => {
    // TODO: Implement email functionality with edge function
    toast({
      title: "Email Feature",
      description: "Email functionality will be implemented soon."
    });
  };

  const updateField = (field: keyof typeof letterData, value: string) => {
    setLetterData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const LetterPreview = () => (
    <div className="max-w-2xl mx-auto p-8 bg-white text-black print:shadow-none shadow-lg">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-primary">Contract Funding Group</h1>
        <p className="text-muted-foreground">Making real estate investments simple</p>
      </div>
      
      <div className="mb-6">
        <p className="mb-2">{new Date().toLocaleDateString()}</p>
        <div>
          <p className="font-semibold">{letterData.recipientName}</p>
          {letterData.recipientAddress && (
            <p className="whitespace-pre-line">{letterData.recipientAddress}</p>
          )}
        </div>
      </div>

      <div className="mb-6">
        <p className="mb-4">Dear {letterData.recipientName || '[Recipient Name]'},</p>
        
        <p className="mb-4">
          Thank you for choosing Contract Funding Group. We strive to make the closing process 
          efficient and painless for all parties involved.
        </p>
        
        <p className="mb-4">
          Below is a checklist of items needed for your cash offer underwriting. Please gather 
          these documents to ensure a smooth and timely closing process:
        </p>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Required Documentation Checklist:</h3>
        {letterData.checklistItems.length > 0 ? (
          <ul className="space-y-2">
            {letterData.checklistItems.map((item) => (
              <li key={item.id} className="flex items-start">
                <span className="inline-block w-4 h-4 border border-gray-400 mr-3 mt-1 flex-shrink-0"></span>
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground italic">No checklist items added yet.</p>
        )}
      </div>

      <div className="mb-6">
        <p className="mb-4">
          Please review this checklist carefully and gather all required documents. If you have 
          any questions or need clarification on any item, please don't hesitate to contact us.
        </p>
        
        <p className="mb-4">
          We appreciate your business and look forward to working with you to complete this transaction.
        </p>
      </div>

      <div>
        <p className="mb-2">Sincerely,</p>
        <p className="font-semibold">Contract Funding Group Team</p>
        <p className="text-sm text-muted-foreground mt-4">
          Contract Funding Group | Real Estate Investment Solutions
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Checklist Letter Generator</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Letter Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="recipientName">Recipient Name</Label>
              <Input
                id="recipientName"
                value={letterData.recipientName}
                onChange={(e) => updateField('recipientName', e.target.value)}
                placeholder="Enter recipient's full name"
              />
            </div>
            
            <div>
              <Label htmlFor="recipientEmail">Recipient Email</Label>
              <Input
                id="recipientEmail"
                type="email"
                value={letterData.recipientEmail}
                onChange={(e) => updateField('recipientEmail', e.target.value)}
                placeholder="Enter recipient's email"
              />
            </div>
            
            <div>
              <Label htmlFor="recipientAddress">Recipient Address</Label>
              <Textarea
                id="recipientAddress"
                value={letterData.recipientAddress}
                onChange={(e) => updateField('recipientAddress', e.target.value)}
                placeholder="Enter recipient's mailing address"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Checklist Items</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                placeholder="Enter checklist item"
                onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
              />
              <Button onClick={addChecklistItem} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {letterData.checklistItems.map((item) => (
                <div key={item.id} className="flex items-center gap-2 p-2 border rounded">
                  <span className="flex-1 text-sm">{item.text}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeChecklistItem(item.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              {letterData.checklistItems.length === 0 && (
                <p className="text-muted-foreground text-sm text-center py-4">
                  No checklist items added yet
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Letter
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Letter Preview</DialogTitle>
                </DialogHeader>
                <LetterPreview />
              </DialogContent>
            </Dialog>
            
            <Button onClick={handlePrint} variant="outline">
              <Printer className="h-4 w-4 mr-2" />
              Print Letter
            </Button>
            
            <Button onClick={handleEmail} disabled>
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Hidden print version */}
      <div className="hidden print:block">
        <LetterPreview />
      </div>
    </div>
  );
};

export default ChecklistLetter;